import React from 'react';

type ProfileInformationSkeletonProps = {
  variant?: 'desktop' | 'mobile';
};

const ShimmerOverlay = () => (
  <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none" />
);

const ProfileInformationSkeleton: React.FC<ProfileInformationSkeletonProps> = ({
  variant = 'desktop',
}) => {
  const isDesktop = variant === 'desktop';

  if (isDesktop) {
    return (
      <div
        className="flex flex-col w-full border border-[#E9E9E9] rounded-xl my-5 p-10 bg-white relative overflow-hidden"
        role="status"
        aria-label="Loading profile information"
      >
        <ShimmerOverlay />

        <div className="flex flex-col sm:flex-row items-center rounded-xl bg-white">
          <div className="flex-shrink-0 flex items-center justify-center w-[100px] h-[100px] mr-8">
            <div className="h-[85px] w-[85px] rounded-full bg-gray-200/70 border border-[#E9E9E9]/70 animate-pulse" />
          </div>
          <div className="flex flex-col flex-1 items-start justify-center gap-3 w-full">
            <div className="h-5 bg-gray-200/70 rounded w-32 animate-pulse" />
            <div className="h-4 bg-gray-200/70 rounded w-48 animate-pulse" />
            <div className="flex flex-row gap-4 mt-2">
              <div className="h-4 bg-gray-200/70 rounded w-40 animate-pulse" />
              <div className="h-4 bg-gray-200/70 rounded w-36 animate-pulse" />
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2].map((field) => (
              <div key={field} className="flex flex-col gap-2">
                <div className="h-4 bg-gray-200/70 rounded w-24 animate-pulse" />
                <div className="h-[50px] bg-gray-200/70 rounded-md animate-pulse" />
              </div>
            ))}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <div className="h-4 bg-gray-200/70 rounded w-20 animate-pulse" />
              <div className="h-[50px] bg-gray-200/70 rounded-md animate-pulse" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:col-span-2">
              {[1, 2, 3].map((field) => (
                <div key={field} className="flex flex-col gap-2">
                  <div className="h-4 bg-gray-200/70 rounded w-16 animate-pulse" />
                  <div className="h-[50px] bg-gray-200/70 rounded-md animate-pulse" />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8 h-[44px] bg-gray-200/70 rounded-md w-56 animate-pulse" />
        </div>
        <span className="sr-only">Loading profile information...</span>
      </div>
    );
  }

  return (
    <div className="w-full" role="status" aria-label="Loading profile information">
      <div className="h-5 bg-gray-200/70 rounded w-28 mb-2 animate-pulse" />

      <div className="flex flex-col w-full rounded-xl mb-5 pb-5 bg-white relative overflow-hidden">
        <ShimmerOverlay />

        <div
          className="grid grid-cols-2 rounded-2xl border border-[#E9E9E9] bg-white"
          style={{ gridTemplateRows: 'minmax(120px, auto) auto' }}
        >
          <div className="col-span-2 flex flex-row items-center p-5 min-h-[120px] gap-4">
            <div className="h-[50px] w-[50px] rounded-full bg-gray-200/70 border border-[#E9E9E9]/70 animate-pulse flex-shrink-0" />
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-5 bg-gray-200/70 rounded w-28 animate-pulse" />
              <div className="h-4 bg-gray-200/70 rounded w-44 animate-pulse" />
            </div>
          </div>
          <div className="flex flex-row border-t border-[#E9E9E9] gap-0 w-full col-span-2 py-3 px-2">
            <div className="flex-1 h-10 bg-gray-200/70 rounded-l-md animate-pulse mx-1" />
            <div className="flex-1 h-10 bg-gray-200/70 rounded-r-md animate-pulse mx-1" />
          </div>
        </div>

        <div className="mt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2].map((field) => (
              <div key={field} className="flex flex-col gap-2">
                <div className="h-4 bg-gray-200/70 rounded w-24 animate-pulse" />
                <div className="h-[50px] bg-gray-200/70 rounded-md animate-pulse" />
              </div>
            ))}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <div className="h-4 bg-gray-200/70 rounded w-20 animate-pulse" />
              <div className="h-[50px] bg-gray-200/70 rounded-md animate-pulse" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:col-span-2">
              {[1, 2, 3].map((field) => (
                <div key={field} className="flex flex-col gap-2">
                  <div className="h-4 bg-gray-200/70 rounded w-16 animate-pulse" />
                  <div className="h-[50px] bg-gray-200/70 rounded-md animate-pulse" />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8 h-[44px] bg-gray-200/70 rounded-md w-full animate-pulse" />
        </div>
      </div>
      <span className="sr-only">Loading profile information...</span>
    </div>
  );
};

export default ProfileInformationSkeleton;
